import { ENGLISH_MERGE_TAG_MAP, MERGE_TAG_MAP } from './emailMergeTagLabels';

import type { Descendant } from 'slate';

export {
    CANONICAL_COMPANY_NAME_TAG,
    CANONICAL_REVIEWEE_NAME_TAG,
    MERGE_TAG_MAP,
} from './emailMergeTagLabels';

type SlateTextNode = { text: string };

type SlateElementNode = { children: Descendant[] };

const getMergeTagMap = (language: string) => MERGE_TAG_MAP[language] ?? ENGLISH_MERGE_TAG_MAP;

const normalizeLanguageCode = (language: string): string => {
    const normalized = language.replace(/-/g, '_');
    if (normalized in MERGE_TAG_MAP) {
        return normalized;
    }

    return normalized.split('_')[0] || 'en';
};

export const localizeMergeTagsForUi = (text: string, language: string): string => {
    const lang = normalizeLanguageCode(language);
    const map = getMergeTagMap(lang);

    return Object.entries(map).reduce(
        (result, [canonicalToken, localizedToken]) =>
            result.replace(new RegExp(escapeRegExp(canonicalToken), 'g'), localizedToken),
        text,
    );
};

export const normalizeMergeTagsForStorage = (text: string): string => {
    let result = text;

    Object.values(MERGE_TAG_MAP).forEach((languageMap) => {
        Object.entries(languageMap).forEach(([canonicalToken, localizedToken]) => {
            if (localizedToken !== canonicalToken) {
                result = result.replace(
                    new RegExp(escapeRegExp(localizedToken), 'g'),
                    canonicalToken,
                );
            }
        });
    });

    return result;
};

const hasText = (node: Descendant): node is SlateTextNode =>
    typeof (node as { text?: string }).text === 'string';

const hasChildren = (node: Descendant): boolean =>
    Array.isArray((node as { children?: Descendant[] }).children);

const replaceTextInDescendant = (
    node: Descendant,
    replace: (text: string) => string,
): Descendant => {
    if (hasText(node)) {
        return { ...node, text: replace(node.text) } as Descendant;
    }

    if (hasChildren(node)) {
        const elementNode = node as SlateElementNode & Descendant;
        return {
            ...node,
            children: elementNode.children.map((child) => replaceTextInDescendant(child, replace)),
        } as Descendant;
    }

    return node;
};

export const localizeMergeTagsForUiSlate = (value: Descendant[], language: string): Descendant[] =>
    value.map((node) =>
        replaceTextInDescendant(node, (text) => localizeMergeTagsForUi(text, language)),
    );

export const normalizeMergeTagsForStorageSlate = (value: Descendant[]): Descendant[] =>
    value.map((node) =>
        replaceTextInDescendant(node, (text) => normalizeMergeTagsForStorage(text)),
    );

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
